import { compressImageFile } from "@bto/shared";
import { IFileWithMeta } from "react-dropzone-uploader";
import { BASE_URL } from "../../../utils/requestUtils";
import { PortalBillStatusType, PortalBillType } from "../types";
import { toBase64 } from "../../../utils/helpers";
import { InsuranceUploadType } from "../../Insurance/types";

export interface BillingLinkAuthorizationStatus {
  authorized: boolean;
  requires_dob: boolean;
  csrf_token: string;
}

export const BillingLinkVerificationAttemptsExhaustedCode =
  "billing_verification_attempts_exhausted";

let billingLinkCsrfToken: string | undefined;
let billingLinkAccessToken: string | undefined;

const billingApiFetch = (
  input: RequestInfo | URL,
  init?: RequestInit,
): Promise<Response> => {
  const headers = new Headers(init?.headers);
  if (billingLinkAccessToken && !headers.has("Authorization")) {
    headers.set("Authorization", `Bearer ${billingLinkAccessToken}`);
  }

  const method = (init?.method ?? "GET").toUpperCase();
  if (
    billingLinkCsrfToken &&
    !["GET", "HEAD", "OPTIONS"].includes(method) &&
    !headers.has("X-CSRFToken")
  ) {
    headers.set("X-CSRFToken", billingLinkCsrfToken);
  }

  return fetch(input, {
    ...init,
    headers,
    credentials: "include",
  });
};

export const GetBillingLinkAuthorization = async (
  upfront_pk: string,
  accessToken?: string,
  signal?: AbortSignal,
): Promise<BillingLinkAuthorizationStatus> => {
  billingLinkAccessToken = accessToken;
  billingLinkCsrfToken = undefined;
  const response = await billingApiFetch(
    `${BASE_URL}/api/v1/u_payment/${upfront_pk}/authorization/`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
      signal,
    },
  );
  if (!response.ok) {
    throw new Error("Billing-link authorization failed");
  }
  const authorization =
    (await response.json()) as BillingLinkAuthorizationStatus;
  billingLinkCsrfToken = authorization.csrf_token;
  return authorization;
};

export const VerifyBillingLinkDob = async (
  upfront_pk: string,
  dateOfBirth: string,
): Promise<Response> =>
  billingApiFetch(`${BASE_URL}/api/v1/u_payment/${upfront_pk}/verify_dob/`, {
    method: "POST",
    body: JSON.stringify({ date_of_birth: dateOfBirth }),
    headers: {
      "Content-Type": "application/json",
    },
  });

export const showUpfrontPayment = (
  portal_bill_status: PortalBillStatusType,
): boolean => {
  // only bills with one of these statuses should be shown to the patient
  const allowedStatuses = [
    "outstanding",
    "paid_in_full",
    "failure",
    "prescreened",
    "in_progress",
    "has_secondary_insurance",
    "primary_ins_provided",
    "secondary_ins_provided",
    "medicaid_or_hmo_ins_provided",
    "fa_fully_covered",
    "has_medicaid",
    "refunded",
    "oa100_only",
    "no_oa_100_payment_required",
  ];
  return allowedStatuses.includes(portal_bill_status);
};

export const getCentsToDollars = (
  cents?: number,
  hideCents?: boolean,
): string =>
  cents !== undefined
    ? (cents / 100).toLocaleString("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: hideCents ? 0 : undefined,
        maximumFractionDigits: hideCents ? 0 : undefined,
      })
    : "";

const getUpfrontQueryParam = (queryRouteValue?: string) => {
  if (queryRouteValue === "q") {
    return "?utm_source=paper_statement";
  }
  if (queryRouteValue) {
    return "?is_billing_admin=true";
  }
  return "";
};

export const GetBillPreviews = async (
  access_token: string,
  signal?: AbortSignal,
): Promise<Response> =>
  fetch(`${BASE_URL}/api/v1/bill-preview`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${access_token}`,
    },
    signal,
  });

export const GetUpfrontPayment = async (
  upfront_pk: string,
  queryRouteValue?: string,
  signal?: AbortSignal,
): Promise<Response> =>
  billingApiFetch(
    `${BASE_URL}/api/v1/u_payment/${upfront_pk}/${getUpfrontQueryParam(
      queryRouteValue,
    )}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
      signal,
    },
  );

export const PostUpfrontPaymentSuccess = async (
  upfront_pk: string,
  client_secret: string,
  email: string,
  is_saved: boolean,
  queryRouteValue?: string,
  payment_method_id?: string,
): Promise<Response> =>
  billingApiFetch(
    `${BASE_URL}/api/v1/u_payment/${upfront_pk}/submit_one_time_payment/${getUpfrontQueryParam(
      queryRouteValue,
    )}`,
    {
      method: "POST",
      body: JSON.stringify({
        client_secret,
        email,
        is_saved,
        payment_method_id,
      }),
      headers: {
        "Content-Type": "application/json",
      },
    },
  );

export const PostOA100PaymentSuccess = async (
  upfront_pk: string,
  client_secret: string,
  email: string,
  payment_method_id?: string,
): Promise<Response> =>
  billingApiFetch(
    `${BASE_URL}/api/v1/u_payment/${upfront_pk}/record_oa_100_payment/`,
    {
      method: "POST",
      body: JSON.stringify({
        client_secret,
        email,
        is_saved: false,
        payment_method_id,
      }),
      headers: {
        "Content-Type": "application/json",
      },
    },
  );

export const GetUpfrontPaymentReceipt = async (
  upfront_pk: string,
  email?: string,
  queryRouteValue?: string,
): Promise<Response> =>
  billingApiFetch(
    `${BASE_URL}/api/v1/u_payment/${upfront_pk}/receipt/${getUpfrontQueryParam(
      queryRouteValue,
    )}`,
    {
      method: "POST",
      body: JSON.stringify({
        email,
      }),
      headers: {
        "Content-Type": "application/json",
      },
    },
  );

export const GetOA100Receipt = async (
  upfront_pk: string,
  email?: string,
  queryRouteValue?: string,
): Promise<Response> =>
  billingApiFetch(
    `${BASE_URL}/api/v1/u_payment/${upfront_pk}/oa_100_receipt/${getUpfrontQueryParam(
      queryRouteValue,
    )}`,
    {
      method: "POST",
      body: JSON.stringify({
        email,
      }),
      headers: {
        "Content-Type": "application/json",
      },
    },
  );

export const GetUpfrontPaymentFinancialAssistance = async (
  upfront_pk: string,
  financial: UpfrontPaymenFinancialAssistance,
  financialStatementFile: IFileWithMeta | null,
  queryRouteValue?: string,
): Promise<Response> => {
  let requestBody = { ...financial };
  if (financialStatementFile) {
    const compressedFile = await compressImageFile(financialStatementFile.file);
    const base64FinancialStatementFile = await toBase64(compressedFile);
    requestBody = {
      ...financial,
      financial_statement_file: base64FinancialStatementFile,
    };
  }
  return billingApiFetch(
    `${BASE_URL}/api/v1/u_payment/${upfront_pk}/financial_assistance_application/${getUpfrontQueryParam(
      queryRouteValue,
    )}`,
    {
      method: "POST",
      body: JSON.stringify(requestBody),
      headers: {
        "Content-Type": "application/json",
      },
    },
  );
};

export const GetFinancialAssistanceIncomeTiers = async (
  upfront_pk: string,
): Promise<Response> =>
  billingApiFetch(
    `${BASE_URL}/api/v1/u_payment/${upfront_pk}/financial_assistance_income_tiers/`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    },
  );

export const GetFinancialAssistanceLocalities = async (
  upfront_pk: string,
): Promise<Response> =>
  billingApiFetch(
    `${BASE_URL}/api/v1/u_payment/${upfront_pk}/financial_assistance_localities/`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    },
  );

export const GetUpfrontPaymentPaperStatementUrls = async (
  token: string,
  upfront_pk: string,
): Promise<Response> =>
  fetch(`${BASE_URL}/api/v1/payment/${upfront_pk}/paper_statements/`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });

export const GetPreviewUpfrontPaymentPlan = async (
  upfront_pk: string,
  queryRouteValue?: string,
): Promise<Response> =>
  billingApiFetch(
    `${BASE_URL}/api/v1/u_payment/${upfront_pk}/preview_payment_plan/${getUpfrontQueryParam(
      queryRouteValue,
    )}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    },
  );

export const GetSavedCardsUpfront = async (
  upfront_pk: string,
  access_token: string,
): Promise<Response> =>
  fetch(`${BASE_URL}/api/v1/payment/${upfront_pk}/payment_methods/`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${access_token}`,
    },
  });

export const GetCurrentPaymentMethodForPaymentPlanUpfront = async (
  upfront_pk: string,
): Promise<Response> =>
  billingApiFetch(
    `${BASE_URL}/api/v1/u_payment/${upfront_pk}/current_payment_method/`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    },
  );

export const PayWithSavedCardUpfront = async (
  upfront_pk: string,
  payment_method_id: string,
  email: string,
  payment_type: "pay_in_full" | "payment_plan",
  access_token: string,
): Promise<Response> =>
  fetch(`${BASE_URL}/api/v1/payment/${upfront_pk}/submit_saved_payment/`, {
    method: "POST",
    body: JSON.stringify({ payment_method_id, email, payment_type }),
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${access_token}`,
    },
  });

export const RemoveUpfrontPayment = async (
  upfront_pk: string,
  payment_method_id?: string,
  access_token?: string,
): Promise<Response> =>
  fetch(`${BASE_URL}/api/v1/payment/${upfront_pk}/remove_payment_method`, {
    method: "DELETE",
    body: JSON.stringify({ payment_method_id }),
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${access_token}`,
    },
  });

export const PostUpfrontPaymentPlanSuccess = async (
  upfront_pk: string,
  payment_method_id: string,
  email: string,
  is_saved: boolean,
  queryRouteValue?: string,
): Promise<Response> =>
  billingApiFetch(
    `${BASE_URL}/api/v1/u_payment/${upfront_pk}/submit_payment_plan/${getUpfrontQueryParam(
      queryRouteValue,
    )}`,
    {
      method: "POST",
      body: JSON.stringify({ payment_method_id, email, is_saved }),
      headers: {
        "Content-Type": "application/json",
      },
    },
  );

export const PostUpfrontPaymentPlanPayoff = async (
  upfront_pk: string,
  payment_method_id: string,
  queryRouteValue?: string,
): Promise<Response> =>
  billingApiFetch(
    `${BASE_URL}/api/v1/u_payment/${upfront_pk}/submit_payment_plan_payoff/${getUpfrontQueryParam(
      queryRouteValue,
    )}`,
    {
      method: "POST",
      body: JSON.stringify({ payment_method_id }),
      headers: {
        "Content-Type": "application/json",
      },
    },
  );

export const PayUpfrontPaymentPlanInstallment = async (
  upfront_pk: string,
  installment_id: string,
  installment_amount: number,
  payment_method_id: string,
  queryRouteValue?: string,
): Promise<Response> =>
  billingApiFetch(
    `${BASE_URL}/api/v1/u_payment/${upfront_pk}/pay_payment_plan_installment/${getUpfrontQueryParam(
      queryRouteValue,
    )}`,
    {
      method: "POST",
      body: JSON.stringify({
        installment: {
          id: installment_id,
          amount: installment_amount,
        },
        payment_method: {
          payment_method_id,
        },
      }),
      headers: {
        "Content-Type": "application/json",
      },
    },
  );

export const PostUpfrontPaymentMedicaidHMODismiss = async (
  upfront_pk: string,
  queryRouteValue?: string,
): Promise<Response> =>
  billingApiFetch(
    `${BASE_URL}/api/v1/u_payment/${upfront_pk}/dismiss_medicaid_or_hmo_prompt/${getUpfrontQueryParam(
      queryRouteValue,
    )}`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
    },
  );

export const GetUpfrontPaymentPlan = async (
  upfront_pk: string,
  queryRouteValue?: string,
): Promise<Response> =>
  billingApiFetch(
    `${BASE_URL}/api/v1/u_payment/${upfront_pk}/get_payment_plan/${getUpfrontQueryParam(
      queryRouteValue,
    )}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    },
  );

export const CreateSetupIntent = async (
  upfront_pk: string,
): Promise<Response> =>
  billingApiFetch(
    `${BASE_URL}/api/v1/u_payment/${upfront_pk}/create_setup_intent/`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
    },
  );

export const SavePaymentMethodFromSetupIntent = async (
  upfront_pk: string,
  setupIntentId: string,
): Promise<Response> =>
  billingApiFetch(
    `${BASE_URL}/api/v1/u_payment/${upfront_pk}/save_payment_method_from_setup_intent/`,
    {
      method: "POST",
      body: JSON.stringify({
        setup_intent_id: setupIntentId,
      }),
      headers: {
        "Content-Type": "application/json",
      },
    },
  );

export const SubmitOA100Document = async (
  file_data: string,
  bill_pk: string,
): Promise<{ error: boolean }> => {
  try {
    const response = await billingApiFetch(
      `${BASE_URL}/api/v1/u_payment/${bill_pk}/upload_oa100_document/`,
      {
        method: "POST",
        body: JSON.stringify({ file_data }),
        headers: {
          "Content-Type": "application/json",
        },
      },
    );
    if (response?.ok) {
      return { error: false };
    }
  } catch (e) {
    return { error: true };
  }
  return { error: true };
};

export const SubmitFASupportingDocument = async (
  file_data: string,
  bill_pk: string,
  queryRouteValue?: string,
): Promise<Response> =>
  billingApiFetch(
    `${BASE_URL}/api/v1/u_payment/${bill_pk}/upload_fa_supporting_document/${getUpfrontQueryParam(
      queryRouteValue,
    )}`,
    {
      method: "POST",
      body: JSON.stringify({ file_data }),
      headers: {
        "Content-Type": "application/json",
      },
    },
  );

export const UploadInsuranceCardForBill = async (
  file_data: string,
  bill_pk: string,
  insurance_type?: InsuranceUploadType,
  queryRouteValue?: string,
): Promise<Response> =>
  billingApiFetch(
    `${BASE_URL}/api/v1/u_payment/${bill_pk}/upload_insurance_card/${getUpfrontQueryParam(
      queryRouteValue,
    )}`,
    {
      method: "POST",
      body: JSON.stringify({ file_data, insurance_type }),
      headers: {
        "Content-Type": "application/json",
      },
    },
  );

export const SubmitNewUpfrontInsurance = async (
  s3_urls: string[],
  bill_pk: string,
  insurance_type: InsuranceUploadType,
  queryRouteValue?: string,
): Promise<Response> =>
  billingApiFetch(
    `${BASE_URL}/api/v1/u_payment/${bill_pk}/submit_insurance_cards/${getUpfrontQueryParam(
      queryRouteValue,
    )}`,
    {
      method: "POST",
      body: JSON.stringify({ s3_urls, insurance_type }),
      headers: {
        "Content-Type": "application/json",
      },
    },
  );

export interface UpfrontPaymentPlan {
  installment_id: string;
  epoch: number;
  amount: number;
  status: "succeeded" | "failed" | "processing" | "pending" | "paid" | "draft";
  receipt_url: string;
  pay_now: boolean;
  card_brand: string;
  stripe_sync_status:
    | "succeeded"
    | "failed"
    | "processing"
    | "pending"
    | "retryable"
    | "ignored";
  last_four_digits: string;
}

export interface BillPreview {
  pk: string;
  req_identifier: string;
  date_of_service: string;
  due_date: string;
  portal_bill_status: PortalBillStatusType;
  balance: number;
}

export interface Bill extends BillPreview {
  is_final: boolean;
  paid_amount: number;
  oa100_balance: number;
  provider_name: string;
  is_qualified_for_financial_assistance: boolean;
  has_submitted_financial_assistance: boolean;
  fa_has_supporting_docs: boolean;
  login_url: string;
  signup_url: string;
  is_registered_for_portal: boolean;
  client_secret: string;
  oa_100_client_secret: string;
  oa_100_payment_intent: string;
  is_payment_plan_enabled: boolean;
  oa_100_billing_status: PortalBillStatusType;
  bto_discount: number;
  total_insurance_allowed_amount: number;
  total_insurance_paid_amount: number;
  start_of_payment_amount?: number;
  insurance_name: string;
  is_cash_pay: boolean;
  show_medicaid_or_hmo_prompt: boolean;
  accept_insurance: boolean;
  babypeek_pk?: string;
  show_financial_assistance: boolean;
  insurance_adjustment_wording: string;
  bill_type: PortalBillType;
}

export interface PaperStatementUrls {
  paper_statements: {
    url: string;
    date: string;
  }[];
}

export interface UpfrontPaymentReceiptType {
  date_of_service: string;
  provider_name: string;
  amount_paid: number;
  payment_date: string;
  payment_intent_id: string;
  last_four_digits: string;
  card_brand: string;
  receipt_url: string;
}

interface UpfrontPaymenFinancialAssistance {
  has_medicaid: boolean;
  medicaid_insurance_card_urls: string[];
  household_size: number;
  income: number;
  extenuating_circumstances: string[];
  financial_statement_file?: string;
  locality: string | null;
}

export interface UpfrontPaymenFinancialAssistanceResponse {
  bill: Bill;
  is_qualified: boolean;
}
